// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, useState } from 'react';

import { type WorkOrder, useEvolvePaginatedGet, type WorkOrderItem } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNotNil } from 'helpers/isNotNil';
import { WorkOrderItemBOM } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/WorkOrderItemBOM';
import { getWorkOrderBomColDef } from 'modules/Shop/WorkOrders/WorkOrdersPage/columnDefs';

type Props = {
  workOrder: WorkOrder;
};

const BillOfMaterialsContents = ({ workOrder }: Props) => {
  const { fetchPage, abortController } = useEvolvePaginatedGet(`shop/workOrder/${workOrder.workOrderId}/bom`);

  const colDef = useMemo(() => getWorkOrderBomColDef(), []);

  const { agGridProps } = useServerSideGrid({
    tableName: 'bill-of-materials',
    tableViewType: TABLE_VIEW_TYPE.WORK_ORDER_BILL_OF_MATERIALS,
    fetchPage,
    abortController,
    colDef,
    rowId: (r) => r.rowId,
  });

  return (
    <AgGridContainer>{(applyWrap) => <AgGridReact {...agGridProps} onFirstDataRendered={applyWrap} />}</AgGridContainer>
  );
};

export const BillOfMaterialsModal = (props: Props) => {
  const { workOrder } = props;
  const [opened, setOpened] = useState(false);
  const [workOrderItem, setWorkOrderItem] = useState<WorkOrderItem | null>(null);

  const workOrderItemsPaginatedGet = useEvolvePaginatedGet(`shop/workOrder/item`, {
    defaultConfig: {
      params: {
        workOrderId: workOrder.workOrderId,
        orderBy: 'name:asc',
      },
    },
  });

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpened(true)}>
        View BOM
      </Button>
      <WrappedModal
        miniHeader={workOrder.name}
        title={workOrder.identifier}
        subtitle="Bill of Materials"
        opened={opened}
        onClose={() => setOpened(false)}
        hideModalButtons
        size="70rem"
        tall
        headerRightComponent={
          <SearchableSelect
            value={workOrderItem}
            onChange={setWorkOrderItem}
            paginatedGet={workOrderItemsPaginatedGet}
            getItemLabel={(item) => item.name}
            idKey="workOrderItemId"
            clearable
            placeholder="Search for work order item..."
            miw={250}
          />
        }
      >
        {isNotNil(workOrderItem) ? (
          <WorkOrderItemBOM workOrder={workOrder} workOrderItem={workOrderItem} readOnly />
        ) : (
          <BillOfMaterialsContents {...props} />
        )}
      </WrappedModal>
    </>
  );
};
